// AdManager: a provider waterfall for interstitial + rewarded ads.
//
// Providers are tried in order; the first that fills wins. No provider SDKs are
// bundled, so out of the box the waterfall is empty and calls resolve via the
// no-fill policy below â€” which keeps game flow unblocked during development.
// Real integrations (Google H5, a portal SDK, etc.) register here in init().

import { GoogleH5Provider } from './providers/googleH5Provider.js';
import { AdMobCapacitorProvider } from './providers/admobCapacitorProvider.js';
import { isCapacitorNative } from '../platform/env.js';

export class AdManager {
  constructor(cfg, ctx) {
    this.cfg = cfg;
    this.log = ctx.log;
    this.emit = ctx.emit || (() => {});
    this.providers = [];        // each: { interstitial(placement), rewarded(placement) }
  }

  async init() {
    if (!this.cfg.enabled) { this.log.info('Ads disabled.'); return; }
    for (const p of this.cfg.providers || []) {
      if (p.type === 'googleH5') {
        const provider = new GoogleH5Provider(p, { log: this.log, emit: this.emit });
        this.providers.push(provider);
        this.log.debug('Ad provider registered: googleH5');
        provider.init().catch((err) => this.log.warn('googleH5 init issue', err));
        continue;
      }
      if (p.type === 'admobCapacitor') {
        // Native-only: skip on web so web builds keep using googleH5.
        if (!isCapacitorNative()) {
          this.log.debug('Ad provider skipped (not a Capacitor native platform): admobCapacitor');
          continue;
        }
        const provider = new AdMobCapacitorProvider(p, { log: this.log, emit: this.emit });
        this.providers.push(provider);
        this.log.debug('Ad provider registered: admobCapacitor');
        provider.init().catch((err) => this.log.warn('admobCapacitor init issue', err));
        continue;
      }
      this.log.warn(`Ad provider "${p.type}" configured but no integration is registered; treating as no-fill.`);
    }
    this.log.debug(`AdManager ready with ${this.providers.length} provider(s).`);
  }

  async showInterstitial(placement = 'default') {
    if (!this.cfg.enabled) return { shown: false, reason: 'disabled' };
    let fallbackReason = 'no-fill';
    for (const p of this.providers) {
      try {
        const r = await p.interstitial?.(placement);
        if (r?.shown) { this.emit('ad:interstitial', { placement }); return { ...r, provider: p.name }; }
        if (r?.reason && fallbackReason === 'no-fill') fallbackReason = r.reason;
      } catch (err) {
        fallbackReason = fallbackReason === 'no-fill' ? 'error' : fallbackReason;
        this.log.warn('interstitial provider error', err);
      }
    }
    this.log.debug(`interstitial: ${fallbackReason} for`, placement);
    return { shown: false, reason: fallbackReason };
  }

  async showRewarded(placement = 'default') {
    if (!this.cfg.enabled) return { shown: false, granted: false, reason: 'disabled' };
    let fallbackReason = 'no-fill';
    for (const p of this.providers) {
      try {
        const r = await p.rewarded?.(placement);
        if (r?.shown) {
          const granted = !!r.granted;
          this.emit('ad:rewarded', { placement, granted });
          return { granted, shown: true, provider: p.name };
        }
        if (r?.reason && fallbackReason === 'no-fill') fallbackReason = r.reason;
      } catch (err) {
        fallbackReason = fallbackReason === 'no-fill' ? 'error' : fallbackReason;
        this.log.warn('rewarded provider error', err);
      }
    }
    // No provider filled. Honor the configured policy so dev flow isn't blocked.
    const granted = !!this.cfg.grantRewardOnNoFill;
    this.log.debug(`rewarded: ${fallbackReason} for ${placement} -> granted=${granted}`);
    return { granted, shown: false, reason: fallbackReason };
  }

  // Native banner control (e.g. AdMob on Capacitor). No-op/graceful where no
  // provider supports banners (e.g. web).
  async showBanner(placement = 'banner') {
    if (!this.cfg.enabled) return { shown: false, reason: 'disabled' };
    for (const p of this.providers) {
      if (typeof p.showBanner !== 'function') continue;
      try { const r = await p.showBanner(placement); return { ...r, provider: p.name }; }
      catch (err) { this.log.warn('showBanner provider error', err); return { shown: false, reason: 'error' }; }
    }
    return { shown: false, reason: 'unsupported' };
  }

  async hideBanner(placement = 'banner') {
    for (const p of this.providers) {
      if (typeof p.hideBanner !== 'function') continue;
      try { return await p.hideBanner(placement); }
      catch (err) { this.log.warn('hideBanner provider error', err); return { hidden: false, reason: 'error' }; }
    }
    return { hidden: false, reason: 'unsupported' };
  }
}


